import React, { useEffect, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Button, Tabs, Tooltip } from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';
import useSectionData from '../hooks/useSectionData';
import ConductorBrief from './dashboard/ConductorBrief';
import KpiRow from './dashboard/KpiRow';
import RevenueByStreamCard from './dashboard/RevenueByStreamCard';
import RevenueMixCard from './dashboard/RevenueMixCard';
import BookingPipelineCard from './dashboard/BookingPipelineCard';
import ShowPacingCard from './dashboard/ShowPacingCard';
import VenuesCard from './dashboard/VenuesCard';
import NeedsAttentionCard from './dashboard/NeedsAttentionCard';
import MoneyCard from './dashboard/MoneyCard';
import SalesByChannelCard from './dashboard/SalesByChannelCard';
import AudienceCard from './dashboard/AudienceCard';
import MarketingImpactCard from './dashboard/MarketingImpactCard';
import MembershipsPacksCard from './dashboard/MembershipsPacksCard';
import { gridStyle } from './dashboard/layout';
import { DEFAULT_KPI_KEYS, showYearOverYear } from '../utils/dashboardUtils';
import { withRoomFilterGroup } from '../constants/salesFiltersConfig';
import {
  errorText, toastError, toastInfo, toastSuccess,
} from '../../../shared/toastHelper';

const axios = require('axios').default;

const COUNT_BY_TABS = [
  { value: 'order_date', label: 'Date Created' },
  { value: 'event_date', label: 'Event Date' },
];
const NO_PARAMS = {};

function ComingSoonAction({ children }) {
  return (
    <Tooltip title="Coming soon">
      <span>
        <Button variant="secondary" size="sm" disabled>{children}</Button>
      </span>
    </Tooltip>
  );
}

function ReportingDashboardNewPage({
  team, venueOwnerships, roomOptions, csrfToken, kpiKeys, layoutLockedMessage,
}) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('month_to_date');
  const [dateMode, setDateMode] = useState('event_date');
  const [savedKpiKeys, setSavedKpiKeys] = useState(kpiKeys || DEFAULT_KPI_KEYS);
  const [draftKpiKeys, setDraftKpiKeys] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);
  const editingLayout = draftKpiKeys !== null;

  const base = `/teams/${team.id}/reporting`;

  useEffect(() => {
    if (!editingLayout || savingLayout) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) setDraftKpiKeys(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editingLayout, savingLayout]);

  const saveLayout = () => {
    setSavingLayout(true);
    axios.patch(`${base}/dashboard_layout.json`, { kpi_keys: draftKpiKeys }, { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then(({ data }) => {
        setSavedKpiKeys(data.kpi_keys || DEFAULT_KPI_KEYS);
        setDraftKpiKeys(null);
        toastSuccess('Dashboard layout saved');
      })
      .catch((err) => toastError(errorText(err, 'Could not save your dashboard layout.')))
      .finally(() => setSavingLayout(false));
  };
  const startCustomizing = () => {
    if (layoutLockedMessage) {
      toastInfo(layoutLockedMessage);
      return;
    }
    setDraftKpiKeys([...savedKpiKeys]);
  };
  const draftIsDefault = editingLayout && draftKpiKeys.join() === DEFAULT_KPI_KEYS.join();

  const filterGroups = useMemo(() => {
    if (!venueOwnerships || venueOwnerships.length === 0) return [];
    return withRoomFilterGroup([{
      label: 'Venues',
      filters: [{
        id: 'venue_ownership_id',
        label: 'Venue',
        type: 'checkbox',
        options: venueOwnerships.map((vo) => ({ value: String(vo.id), label: vo.name || vo.venue_name || `Venue ${vo.id}` })),
      }],
    }], roomOptions);
  }, [venueOwnerships, roomOptions]);

  const venueParams = useMemo(() => (
    activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0
      ? { 'venue_ownership_id[]': activeFilters.venue_ownership_id }
      : {}
  ), [activeFilters]);

  const roomFiltered = activeFilters.room_ids?.length > 0;
  const filterParams = useMemo(() => (
    roomFiltered ? { ...venueParams, 'room_ids[]': activeFilters.room_ids } : venueParams
  ), [venueParams, roomFiltered, activeFilters]);

  const purchaseParams = useMemo(() => ({
    start_date: dateRange?.start,
    end_date: dateRange?.end,
    prior_start_date: comparison.start,
    prior_end_date: comparison.end,
    ...filterParams,
  }), [dateRange, comparison, filterParams]);

  const params = useMemo(() => ({ ...purchaseParams, date_mode: dateMode }), [purchaseParams, dateMode]);

  const upcomingParams = useMemo(() => ({ page: 1, per_page: 50, sort_direction: 'asc', ...filterParams }), [filterParams]);

  const insights = useSectionData('/conductor/venue_chat/insights', NO_PARAMS);
  const kpis = useSectionData(`${base}/dashboard_kpis.json`, params);
  const revenue = useSectionData(`${base}/dashboard_revenue_by_stream.json`, params);
  const booking = useSectionData(`${base}/booking_kpis.json`, params);
  const upcoming = useSectionData(`${base}/upcoming_events.json`, upcomingParams, { lazy: true });
  const venues = useSectionData(`${base}/events_by_venue.json`, params, { lazy: true });
  const attention = useSectionData(`${base}/dashboard_attention.json`, venueParams);
  const money = useSectionData(`${base}/dashboard_money.json`, params, { lazy: true });
  const channels = useSectionData(`${base}/dashboard_sales_channels.json`, purchaseParams, { lazy: true });
  const audience = useSectionData(`${base}/dashboard_audience.json`, purchaseParams, { lazy: true });
  const marketing = useSectionData(`${base}/dashboard_marketing_impact.json`, purchaseParams, { lazy: true });
  const memberships = useSectionData(`${base}/dashboard_memberships.json`, purchaseParams, { lazy: true });
  const purchaseCaption = dateMode === 'event_date';

  const cmpLabel = comparison.label;
  const yearOverYear = showYearOverYear(dateRange);

  const venueQuery = (activeFilters.venue_ownership_id || []).map((id) => `&venue_ownership_id[]=${encodeURIComponent(id)}`).join('');

  return (
    <ReportingPageLayout
      team={team}
      activeKey="dashboard"
      title="Dashboard"
      subtitle="Shows, sales, audience, and money across every venue."
      headerActions={editingLayout ? (
        <>
          <Button variant="tertiary" size="sm" disabled={draftIsDefault || savingLayout} onClick={() => setDraftKpiKeys([...DEFAULT_KPI_KEYS])}>Reset to default</Button>
          <Button variant="secondary" size="sm" disabled={savingLayout} onClick={() => setDraftKpiKeys(null)}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={savingLayout} onClick={saveLayout}>{savingLayout ? 'Saving…' : 'Save layout'}</Button>
        </>
      ) : (
        <>
          <Button variant="secondary" size="sm" onClick={startCustomizing}>Customize</Button>
          <ComingSoonAction>Email me this weekly</ComingSoonAction>
          <ComingSoonAction>Export</ComingSoonAction>
        </>
      )}
      toolbarExtras={(
        <div className="d-flex align-items-center ml-auto" style={{ gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary)' }}>Count by:</span>
          <Tabs variant="button-border" size="sm" collapseBelow={0} items={COUNT_BY_TABS} value={dateMode} onChange={setDateMode} />
        </div>
      )}
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Dashboard"
      filterSubtitle="Apply filters to narrow dashboard data."
    >
      <ConductorBrief insights={insights.data?.insights} isMobile={isMobile} />

      <ToastContainer />
      <KpiRow
        data={kpis.data}
        loading={kpis.loading}
        error={kpis.error}
        comparisonLabel={cmpLabel}
        isMobile={isMobile}
        dateMode={dateMode}
        yearOverYear={yearOverYear}
        links={{ tickets_sold: `${base}/dashboard/sales-tickets` }}
        keys={draftKpiKeys || savedKpiKeys}
        editing={editingLayout}
        onChange={setDraftKpiKeys}
      />

      <div style={gridStyle(isMobile, 'minmax(0, 2fr) minmax(0, 1fr)')}>
        <RevenueByStreamCard data={revenue.data} loading={revenue.loading} error={revenue.error} comparisonLabel={cmpLabel} href={`${base}/dashboard/sales`} yearOverYear={yearOverYear} />
        <RevenueMixCard data={revenue.data} loading={revenue.loading} error={revenue.error} comparisonLabel={cmpLabel} href={`/teams/${team.id}/transactions`} />
      </div>

      <div style={gridStyle(isMobile, 'repeat(2, minmax(0, 1fr))')}>
        <NeedsAttentionCard rows={attention.data} loading={attention.loading} error={attention.error} allRooms={roomFiltered} />
        <BookingPipelineCard
          booking={booking.data}
          kpis={kpis.data}
          loading={booking.loading || kpis.loading}
          dateModeLabel={dateMode === 'event_date' ? 'By event date' : 'By date created'}
          crossBasis={dateMode === 'order_date'}
          href={`${base}/booking`}
        />
      </div>

      <div ref={upcoming.ref} style={{ marginBottom: 16 }}>
        <ShowPacingCard
          events={upcoming.data?.events}
          loading={upcoming.loading}
          error={upcoming.error}
          showTrackerHref={`${base}/marketing/show-tracker`}
          totalUpcoming={upcoming.data?.pagination?.total_entries}
        />
      </div>

      <div ref={venues.ref} style={gridStyle(isMobile, 'repeat(3, minmax(0, 1fr))')}>
        <VenuesCard venues={venues.data} loading={venues.loading} />
        <div ref={channels.ref} style={{ height: '100%' }}>
          <SalesByChannelCard
            data={channels.data}
            loading={channels.loading}
            error={channels.error}
            href={`${base}/dashboard/sales-by-source?start_date=${dateRange?.start}&end_date=${dateRange?.end}`}
            showPurchaseCaption={purchaseCaption}
          />
        </div>
        <div ref={audience.ref} style={{ height: '100%' }}>
          <AudienceCard
            data={audience.data}
            loading={audience.loading}
            error={audience.error}
            href={`${base}/marketing/demographics`}
            showPurchaseCaption={purchaseCaption}
          />
        </div>
      </div>

      <div style={gridStyle(isMobile, 'repeat(3, minmax(0, 1fr))')}>
        <div ref={marketing.ref} style={{ height: '100%' }}>
          <MarketingImpactCard
            data={marketing.data}
            loading={marketing.loading}
            error={marketing.error}
            base={base}
            showPurchaseCaption={purchaseCaption}
          />
        </div>
        <div ref={memberships.ref} style={{ height: '100%' }}>
          <MembershipsPacksCard
            data={memberships.data}
            loading={memberships.loading}
            error={memberships.error}
            links={{
              members: `${base}/dashboard/sales/memberships?start_date=${dateRange?.start}&end_date=${dateRange?.end}${venueQuery}`,
              packs: `${base}/dashboard/sales/packs?start_date=${dateRange?.start}&end_date=${dateRange?.end}${venueQuery}`,
              donations: `${base}/donations/lybunt-sybunt?report_type[]=lybunt${venueQuery}`,
            }}
            showPurchaseCaption={purchaseCaption}
            allRooms={roomFiltered}
          />
        </div>
        <div ref={money.ref} style={{ height: '100%' }}>
          <MoneyCard data={money.data} loading={money.loading} error={money.error} href={`${base}/finances/payouts`} allRooms={roomFiltered} />
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>
        Revenue is net to organizer after Opendate and processing fees. Comps count as $0.
      </p>
    </ReportingPageLayout>
  );
}

export default ReportingDashboardNewPage;
